/**
 * Worldle «Радар» geometry — pure. The hidden country sits at the centre; a guess
 * is drawn where it lies relative to the target: at its distance, on the side
 * opposite the arrow (the arrow points from the guess TO the target). Same
 * information as the list — the scope only shows it all at once.
 */

/** The server's eight arrows (shared.ts ARROWS order) → bearing in degrees, 0 = north, clockwise. */
const ARROW_DEG: Record<string, number> = {
  "⬆️": 0,
  "↗️": 45,
  "➡️": 90,
  "↘️": 135,
  "⬇️": 180,
  "↙️": 225,
  "⬅️": 270,
  "↖️": 315,
};

export function arrowBearing(dir: string): number | null {
  return ARROW_DEG[dir] ?? null;
}

/** Half of Earth's circumference — the farthest any country can be. */
const MAX_KM = 20_000;
/** Inner radius kept free for the target silhouette, as a fraction of the scope radius. */
export const INNER = 0.24;
const OUTER = 0.93;

/**
 * Polar position of a guess on the scope: `angle` in degrees (0 = up/north,
 * clockwise), `r` as a fraction of the scope radius. Log distance so the
 * interesting near misses get the room. `index` fans guesses that share an arrow
 * a few degrees apart — cosmetic, well inside the arrow's own 45° sector.
 */
export function blipPolar(km: number, dir: string, index: number): { angle: number; r: number } {
  if (km <= 0) return { angle: 0, r: 0 };
  const toward = arrowBearing(dir) ?? 0;
  const spread = ((index * 37) % 17) - 8;
  const angle = (toward + 180 + spread + 360) % 360;
  const t = Math.log1p(km / 150) / Math.log1p(MAX_KM / 150);
  return { angle, r: INNER + (OUTER - INNER) * Math.min(1, t) };
}

/** x/y in a 0..100 box (centre 50,50) for a polar position. */
export function polarToBox(angle: number, r: number): { x: number; y: number } {
  const a = (angle * Math.PI) / 180;
  return { x: 50 + 50 * r * Math.sin(a), y: 50 - 50 * r * Math.cos(a) };
}

export type Heat = "cold" | "warm" | "hot" | "hit";

/** How warm a guess reads: ≥75% = within ~5000 km, ≥90% = within ~2000 km. */
export function heatOf(pct: number): Heat {
  if (pct >= 100) return "hit";
  if (pct >= 90) return "hot";
  if (pct >= 75) return "warm";
  return "cold";
}
